import React, { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { useTranslation } from 'react-i18next'
import { Button, ChevronRightIcon } from '@diversioteam/diversio-ds'
import { useHistory } from 'react-router-dom'
import { parse } from 'query-string'
import { useFeature } from '@growthbook/growthbook-react'

import { AppRoute } from './../../../routing/AppRoute.enum'
import { Footer } from './../Footer'
import { Toast } from './../Toast'
import { ForgotPasswordFields, ForgotPasswordFormData, ForgotPasswordProps } from './forgotPassword.types'
import { forgotPasswordSchema } from './forgotPassword.schema'
import Link from './../../utils/Link'
import diversioLogo from './../../../assets/images/diversio-logo-with-symbol-for-light-bg.svg'
import * as S from './forgotPassword.styles'
import * as ToastStyles from './../Toast/toast.styles'

export const ForgotPassword = ({ isLoading, data, isSuccess, error, isError, onSubmit }: ForgotPasswordProps) => {
    const { t } = useTranslation()
    const history = useHistory()
    const { override_maintenance } = parse(location.search)

    // Temporary variable to show scheduled maintenance message. To be removed after 29th March 2024 deployment
    const showTempMessage = useFeature('scheduled-maintenance-message').on

    /*
     * Temporary variable to disable fields during the scheduled maintenance.
     * To be removed after 29th March 2024 deployment
     * This field can be overriden using a query param `?override_maintenance=true`
     */
    const override = override_maintenance === 'true'
    const shouldDisableFields = useFeature('scheduled-maintenance-disabled-fields').on
    const disabled = override ? false : shouldDisableFields

    // Corresponding messages to be shown during 29th March 2024 deployment
    const maintenanceMessage = 'Upcoming scheduled maintenance on 29th March 2024 from 8AM EST to 2PM EST'
    const disabledMessage = 'Login unavailable for scheduled maintenance on 29th March 2024 from 8AM EST to 2PM EST'

    const { register, handleSubmit, errors } = useForm<ForgotPasswordFormData>({
        resolver: yupResolver(forgotPasswordSchema),
    })

    const handleSubmitCallback = useCallback(
        async (data: ForgotPasswordFormData) => {
            await onSubmit(data)
        },
        [onSubmit],
    )

    const getErrorMessage = (fieldName: ForgotPasswordFields) => {
        return t(errors[fieldName]?.message || '')
    }

    return (
        <S.Container>
            <S.Form onSubmit={handleSubmit(handleSubmitCallback)}>
                {showTempMessage && (
                    <ToastStyles.ToastBox>
                        <S.Toast
                            message={maintenanceMessage}
                            type="success"
                            open
                            buttonLabel="Details"
                            onClick={() => {
                                return history.push({
                                    pathname: AppRoute.ScheduledMaintenance,
                                    search: location.search,
                                })
                            }}
                        />
                    </ToastStyles.ToastBox>
                )}
                {disabled && (
                    <ToastStyles.ToastBox>
                        <S.Toast
                            message={disabledMessage}
                            type="error"
                            open
                            buttonLabel="Details"
                            onClick={() => {
                                return history.push({
                                    pathname: AppRoute.ScheduledMaintenance,
                                    search: location.search,
                                })
                            }}
                        />
                    </ToastStyles.ToastBox>
                )}

                <Toast
                    isLoading={isLoading}
                    data={data}
                    isSuccess={isSuccess}
                    error={error}
                    isError={isError}
                    translationPrefix="AUTH.FORGOT_PASSWORD"
                />

                <S.Logo src={diversioLogo} className="logo" alt="Diversio" />

                <S.Title>{t('AUTH.FORGOT_PASSWORD.TITLE')}</S.Title>

                <S.Description>{t('AUTH.FORGOT_PASSWORD.HELP_TEXT')}</S.Description>

                <S.Fields>
                    <S.TextField
                        id={ForgotPasswordFields.usernameEmail}
                        inputRef={register}
                        error={Boolean(errors[ForgotPasswordFields.usernameEmail])}
                        helperText={getErrorMessage(ForgotPasswordFields.usernameEmail)}
                        name={ForgotPasswordFields.usernameEmail}
                        label={t('AUTH.LOGIN.USERNAME_EMAIL')}
                        type="text"
                        disabled={isLoading || disabled}
                        fullWidth
                    />
                </S.Fields>

                <Button
                    loading={isLoading}
                    type="submit"
                    color="primary"
                    size="large"
                    rightIcon={<ChevronRightIcon type="bold" />}
                    fullWidth
                    disabled={disabled}
                >
                    {t('AUTH.FORGOT_PASSWORD.RESET_PASSWORD')}
                </Button>

                <S.TextLink as={Link} to={{ pathname: AppRoute.Login, search: location.search }} disabled={disabled}>
                    {t('AUTH.FORGOT_PASSWORD.GO_BACK')}
                </S.TextLink>

                <Footer />
            </S.Form>
        </S.Container>
    )
}
